import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut,
  CalendarDays,
  Ruler,
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  Archive,
  Images,
  ImagePlus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LangToggle } from "./LangToggle";
import { DashboardVideos } from "./DashboardVideos";
import { WhatsAppAlertsCard } from "./WhatsAppAlertsCard";
import { OwnerLoginScreen } from "./OwnerLoginScreen";
import { SecurityCard } from "./SecurityCard";

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(lang === "es" ? "es-US" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const money = (n) =>
  n == null
    ? "—"
    : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const AppointmentCard = ({ a, onToggle }) => {
  const { t, lang } = useLang();
  const done = a.status === "done";
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl bg-[#132338] border p-6 transition-colors duration-300 ${
        done ? "border-[#203859] opacity-60" : "border-[#203859]"
      }`}
      data-testid={`appointment-card-${a.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display font-bold text-lg text-white">
          {a.first_name} {a.last_name}
        </h3>
        <span className="rounded-full bg-[#F26522]/15 text-[#F26522] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap">
          {a.date}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-300">{a.time_slot}</p>
      <p className="mt-3 text-sm text-slate-400">
        {a.address} — {a.city}
      </p>
      <p className="mt-1 text-sm text-slate-400">
        {a.service_type}
        {a.square_yards
          ? ` · ${a.square_yards} sq yd · ${money(a.price_min)} – ${money(a.price_max)}`
          : ""}
      </p>
      {a.phone && (
        <a
          href={`tel:${a.phone}`}
          className="mt-2 inline-block text-sm font-semibold text-[#F26522] hover:text-white transition-colors duration-200"
        >
          {a.phone}
        </a>
      )}
      {a.email && (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm" data-testid={`appointment-email-${a.id}`}>
          <a href={`mailto:${a.email}`} className="text-slate-300 hover:text-[#F26522] transition-colors duration-200 break-all">
            {a.email}
          </a>
          <span
            className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] font-bold ${
              a.reminder_sent_at ? "bg-[#25D366]/15 text-[#25D366]" : "bg-white/5 text-slate-500"
            }`}
            data-testid={`appointment-reminder-${a.id}`}
          >
            {a.reminder_sent_at ? t("dash_reminder_sent") : t("dash_reminder_pending")}
          </span>
        </p>
      )}
      {a.notes && (
        <p className="mt-3 rounded-xl bg-[#0E1A2B] p-3 text-xs text-slate-400">
          {a.notes}
        </p>
      )}
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
          {t("dash_received")} {fmtDate(a.created_at, lang)}
        </p>
        <button
          onClick={() => onToggle(a)}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-display font-bold text-xs transition-[background-color,color,transform] duration-200 hover:scale-[1.03] active:scale-95 ${
            done
              ? "border border-white/20 text-slate-300 hover:border-[#F26522] hover:text-[#F26522]"
              : "bg-[#F26522] hover:bg-[#D85312] text-white"
          }`}
          data-testid={`appointment-done-btn-${a.id}`}
        >
          {done ? (
            <>
              <RotateCcw className="h-3.5 w-3.5" /> {t("dash_reopen")}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" /> {t("dash_done")}
            </>
          )}
        </button>
      </div>
    </motion.article>
  );
};

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [user, setUser] = useState(location.state?.user || null);
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [forbidden, setForbidden] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setAuth(true);
      return;
    }
    if (window.location.hash?.includes("session_id=")) return;
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setUser(await res.json());
        setAuth(true);
      } catch {
        setAuth(false);
      }
    };
    check();
  }, [location.state]);

  useEffect(() => {
    if (auth !== true) return;
    const load = async () => {
      const [q, a, g] = await Promise.all([
        fetch(`${API}/owner/quotes`, { credentials: "include" }),
        fetch(`${API}/owner/appointments`, { credentials: "include" }),
        fetch(`${API}/gallery`),
      ]);
      if (q.status === 403 || a.status === 403) {
        setForbidden(true);
        return;
      }
      if (q.ok) setQuotes(await q.json());
      if (a.ok) setAppointments(await a.json());
      if (g.ok) setPhotos(await g.json());
    };
    load();
  }, [auth]);

  const toggleDone = async (a) => {
    const next = a.status === "done" ? "new" : "done";
    try {
      const res = await fetch(`${API}/owner/appointments/${a.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: next }),
      });
      if (!res.ok) throw new Error();
      setAppointments((list) =>
        list.map((x) => (x.id === a.id ? { ...x, status: next } : x))
      );
      toast.success(next === "done" ? t("dash_toast_done") : t("dash_toast_reopen"));
    } catch {
      toast.error(t("dash_toast_fail"));
    }
  };

  const uploadPhoto = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t("dash_upload_fail"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(
        `${API}/owner/photos?caption=${encodeURIComponent(caption)}`,
        { method: "POST", credentials: "include", body: fd }
      );
      if (!res.ok) throw new Error();
      const p = await res.json();
      setPhotos((list) => [p, ...list]);
      setCaption("");
      fileRef.current.value = "";
      toast.success(t("dash_photo_added"));
    } catch {
      toast.error(t("dash_upload_fail"));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (p) => {
    try {
      const res = await fetch(`${API}/owner/photos/${p.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setPhotos((list) => list.filter((x) => x.id !== p.id));
      toast.success(t("dash_photo_removed"));
    } catch {
      toast.error(t("dash_upload_fail"));
    }
  };

  const movePhoto = async (p, dir) => {
    try {
      const res = await fetch(`${API}/owner/photos/${p.id}/move`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ direction: dir }),
      });
      if (!res.ok) throw new Error();
      const g = await fetch(`${API}/gallery`);
      if (g.ok) setPhotos(await g.json());
    } catch {
      toast.error(t("dash_upload_fail"));
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    toast.success(t("dash_signed_out"));
    navigate("/");
  };

  const upcoming = appointments.filter((a) => a.status !== "done");
  const archived = appointments.filter((a) => a.status === "done");

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#0E1A2B] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 animate-pulse">
          {t("dash_checking")}
        </p>
      </div>
    );
  }

  if (auth === false || forbidden) {
    return <OwnerLoginScreen forbidden={forbidden} path="/dashboard" testId="owner-login-screen" />;
  }

  return (
    <div className="min-h-screen bg-[#0E1A2B] grain relative" data-testid="owner-dashboard">
      <header className="border-b border-white/10 bg-[#0E1A2B]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto flex items-center justify-between gap-3 h-20">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo.png"
              alt="PERETE logo"
              className="h-11 w-11 shrink-0 rounded-full ring-2 ring-[#F26522]/70 object-cover"
            />
            <div className="leading-tight min-w-0">
              <span className="block font-display font-extrabold text-white tracking-tight truncate">
                {t("dash_title")}
              </span>
              <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-[#F26522] truncate">
                {user?.email}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LangToggle />
            <button
              onClick={() => navigate("/gastos")}
              className="inline-flex items-center gap-2 rounded-full border border-[#F26522]/60 text-[#F26522] hover:bg-[#F26522] hover:text-white px-3 sm:px-4 py-2 min-h-[40px] text-sm font-semibold transition-colors duration-200"
              data-testid="dashboard-expenses-btn"
            >
              <Wallet className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash_expenses_link")}</span>
            </button>
            <button
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 text-slate-300 hover:text-white hover:border-[#F26522] px-4 py-2 text-sm font-semibold transition-colors duration-200"
              data-testid="dashboard-home-btn"
            >
              <ArrowLeft className="h-4 w-4" /> {t("dash_site")}
            </button>
            <button
              onClick={logout}
              aria-label={t("dash_signout")}
              className="inline-flex items-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white px-3 sm:px-4 py-2 min-h-[40px] text-sm font-bold transition-[background-color,transform] duration-200 hover:scale-[1.03] active:scale-95"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash_signout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-8 sm:py-14">
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-10 sm:mb-12">
          <div className="rounded-2xl sm:rounded-3xl bg-[#132338] border border-[#203859] p-4 sm:p-6" data-testid="stat-quotes">
            <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.1em] sm:tracking-[0.2em] text-slate-400 break-words">
              {t("dash_quotes")}
            </p>
            <p className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-[#F26522]">
              {quotes.length}
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl bg-[#132338] border border-[#203859] p-4 sm:p-6" data-testid="stat-appointments">
            <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.1em] sm:tracking-[0.2em] text-slate-400 break-words">
              {t("dash_upcoming")}
            </p>
            <p className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-[#F26522]">
              {upcoming.length}
            </p>
          </div>
          <div className="rounded-2xl sm:rounded-3xl bg-[#132338] border border-[#203859] p-4 sm:p-6" data-testid="stat-archived">
            <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.1em] sm:tracking-[0.2em] text-slate-400 break-words">
              {t("dash_completed")}
            </p>
            <p className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-slate-400">
              {archived.length}
            </p>
          </div>
        </div>

        <WhatsAppAlertsCard />
        <SecurityCard />

        <section className="mb-14">
          <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <CalendarDays className="h-6 w-6 text-[#F26522]" /> {t("dash_appointments")}
          </h2>
          {upcoming.length === 0 ? (
            <p className="text-slate-400 text-sm" data-testid="appointments-empty">
              {t("dash_empty_appts")}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="appointments-list">
              {upcoming.map((a) => (
                <AppointmentCard key={a.id} a={a} onToggle={toggleDone} />
              ))}
            </div>
          )}
        </section>

        {archived.length > 0 && (
          <section className="mb-14" data-testid="archived-section">
            <h2 className="font-display text-2xl font-bold text-slate-400 mb-6 flex items-center gap-3">
              <Archive className="h-6 w-6 text-slate-500" /> {t("dash_archived")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="archived-list">
              {archived.map((a) => (
                <AppointmentCard key={a.id} a={a} onToggle={toggleDone} />
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Ruler className="h-6 w-6 text-[#F26522]" /> {t("dash_quotes_h")}
          </h2>
          {quotes.length === 0 ? (
            <p className="text-slate-400 text-sm" data-testid="quotes-empty">
              {t("dash_empty_quotes")}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quotes-list">
              {quotes.map((q) => (
                <motion.article
                  key={q.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl bg-[#132338] border border-[#203859] p-6"
                  data-testid={`quote-card-${q.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display font-bold text-lg text-white">{q.name}</h3>
                    <span className="rounded-full bg-[#F26522]/15 text-[#F26522] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap">
                      {q.square_yards} sq yd
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-300">{q.service_type}</p>
                  <p className="mt-2 font-display font-bold text-[#F26522]">
                    {money(q.price_min)} – {money(q.price_max)}
                  </p>
                  <a
                    href={`tel:${q.phone}`}
                    className="mt-2 inline-block text-sm font-semibold text-slate-300 hover:text-[#F26522] transition-colors duration-200"
                  >
                    {q.phone}
                  </a>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                    {t("dash_received")} {fmtDate(q.created_at, lang)}
                  </p>
                </motion.article>
              ))}
            </div>
          )}
        </section>
        <section className="mb-14" data-testid="dash-gallery-section">
          <h2 className="font-display text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <Images className="h-6 w-6 text-[#F26522]" /> {t("dash_gallery")}
          </h2>
          <p className="text-slate-400 text-sm mb-6">{t("dash_gallery_sub")}</p>
          <div className="rounded-2xl bg-[#132338] border border-[#203859] p-6 mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="text-sm text-slate-400 file:mr-4 file:rounded-full file:border-0 file:bg-[#0E1A2B] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#203859] file:cursor-pointer"
              data-testid="dash-photo-input"
            />
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t("dash_caption_ph")}
              className="field-input field-input-dark flex-1"
              data-testid="dash-caption-input"
            />
            <button
              onClick={uploadPhoto}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3 text-sm transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60 whitespace-nowrap"
              data-testid="dash-upload-btn"
            >
              <ImagePlus className="h-4 w-4" />
              {uploading ? t("dash_uploading") : t("dash_upload")}
            </button>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dash-photos-grid">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="relative group overflow-hidden rounded-2xl border border-[#203859]"
                  data-testid={`dash-photo-${p.id}`}
                >
                  <img
                    src={`${API}/files/${p.storage_path}`}
                    alt={p.caption || "Gallery photo"}
                    className="h-40 w-full object-cover"
                  />
                  {p.caption && (
                    <p className="absolute bottom-0 inset-x-0 bg-[#0E1A2B]/85 px-3 py-2 text-xs text-slate-200 truncate">
                      {p.caption}
                    </p>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      onClick={() => movePhoto(p, "left")}
                      aria-label={t("dash_move_left")}
                      className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-[#F26522] transition-colors duration-200"
                      data-testid={`dash-photo-left-${p.id}`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => movePhoto(p, "right")}
                      aria-label={t("dash_move_right")}
                      className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-[#F26522] transition-colors duration-200"
                      data-testid={`dash-photo-right-${p.id}`}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removePhoto(p)}
                      aria-label={t("dash_remove_photo")}
                      className="rounded-full bg-[#0E1A2B]/85 p-2 text-slate-300 hover:text-white hover:bg-red-600 transition-colors duration-200"
                      data-testid={`dash-photo-delete-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        <DashboardVideos />

      </main>
    </div>
  );
}
